"use client";

import React from "react";
import { css, styled } from "styled-components";
import { DepartmentDTO } from "@/data/bruktbilsalgBackend/types/department.types";
import {
  breakpointDesktop,
  GhostButton,
  Icon,
  IconButton,
  Heading2Text,
} from "@gnist/design-system";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import { handleDealerWebUrl } from "../utils/handleDealerWebUrl";
import { getThemeFromDealerName } from "@/design-system/ThemeProvider";
import { LogoBrand } from "@gnist/design-system/foundation/logos/Logo.js";
import { tokens } from "@gnist/themes/tokens.css.js";

const cardColorFromTheme = (theme: LogoBrand) => {
  switch (theme) {
    case "mollerbil":
      return {
        background: tokens.palette["neutral-variant"]["90"],
        color: tokens.palette.primary["10"],
      };
    case "dahles":
    case "autoria":
      return {
        background: tokens.color.secondary,
        color: tokens.color["on-secondary"],
      };
    case "gumpen":
      return {
        background: tokens.color["surface-variant"],
        color: tokens.color["on-surface-variant"],
      };
    case "bilhold":
      return {
        background: tokens.color["inverse-background"],
        color: tokens.color["on-inverse-background"],
      };
    default:
      return {
        background: "",
        color: "",
      };
  }
};

const Card = styled.div<{ $theme: LogoBrand }>`
  ${(props) => css`
    width: auto;
    background-color: ${cardColorFromTheme(props.$theme).background};
    color: ${cardColorFromTheme(props.$theme).color};
    display: flex;
    flex-direction: column;
    margin-top: ${tokens.spacing["3xl"]};
    height: fit-content;
    padding: ${tokens.spacing.xl};
    border-radius: ${tokens.radius.card};

    @media screen and (min-width: ${breakpointDesktop}) {
      width: 300px;
      min-width: 300px;
    }
  `}
`;

const TitleWrapper = styled.div`
  margin: 0 0 ${tokens.spacing.l};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${tokens.spacing.s};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;

  dt {
    font-size: ${tokens.typeface.size.s};
  }
  dd {
    margin: 0;
  }
  a {
    margin-top: ${tokens.spacing.base};
  }
`;

const HomepageButtonWrapper = styled.div<{ $theme: LogoBrand }>`
  ${(props) => css`
    > a {
      width: 100%;
      box-sizing: border-box;
      border-color: ${cardColorFromTheme(props.$theme).color};
      color: ${cardColorFromTheme(props.$theme).color};
    }
  `}
`;

interface Props {
  dealer: DepartmentDTO;
  departmentSlug?: string;
}

export const DealerInformation = ({ dealer, departmentSlug }: Props) => {
  const infoItems = [
    {
      label: "Adresse",
      value: `${dealer.address1}, ${dealer.zipCode} ${dealer.city}`,
      directions:
        dealer.latitude &&
        dealer.longitude &&
        `${dealer.latitude},${dealer.longitude}`,
    },
    { label: "E-postadresse", value: dealer.email, directions: "" },
    {
      label: "Telefon",
      value: formatPhoneNumber(dealer.phone, true),
      directions: "",
    },
    {
      label: "Mobil",
      value: formatPhoneNumber(dealer.mobilePhone),
      directions: "",
    },
  ];

  const dealerWebUrl = handleDealerWebUrl(dealer.web);
  const theme = () => getThemeFromDealerName(departmentSlug || null);

  return (
    <Card $theme={theme() as LogoBrand}>
      <TitleWrapper>
        <Heading2Text $as="h2">{dealer.departmentName}</Heading2Text>
      </TitleWrapper>
      <Content>
        {infoItems.map(
          (item) =>
            item.value && (
              <InfoItem key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
                {item.directions && (
                  <IconButton
                    icon="external"
                    label="Veibeskrivelse"
                    $as="a"
                    target="_blank"
                    showLabel="left"
                    href={`https://www.google.com/maps/dir/?q=${item.directions}`}
                  />
                )}
              </InfoItem>
            ),
        )}
        {dealerWebUrl && (
          <HomepageButtonWrapper $theme={theme() as LogoBrand}>
            <GhostButton $as="a" href={dealerWebUrl} target="_blank">
              Gå til hjemmeside <Icon icon="external" />
            </GhostButton>
          </HomepageButtonWrapper>
        )}
        {dealer.openHours && (
          <InfoItem>
            <dt>Åpningstider</dt>
            <dd>{dealer.openHours}</dd>
          </InfoItem>
        )}
      </Content>
    </Card>
  );
};
